// Pure rules for who may use Arabic and which stored language survives.
// Kept separate from React so the rules are directly testable.

export type StoredLanguage = "en" | "ar" | null;

export interface ArabicAvailability {
  isLive: boolean;
  previewMode: boolean;
  isAdmin: boolean;
}

/**
 * Arabic is available to everyone when live, or to admins only while in
 * preview mode. Any unresolved/error state must be treated as unavailable
 * by the caller (fail closed).
 */
export const isArabicAvailableFor = ({
  isLive,
  previewMode,
  isAdmin,
}: ArabicAvailability): boolean => isLive || (previewMode && isAdmin);

/**
 * Decides the active language once settings AND the caller's role are known.
 * A stored Arabic choice is honoured only when Arabic is available to this
 * user; otherwise it is downgraded to English and `downgrade` tells the
 * caller to persist the downgrade so signed-out/non-admin sessions never
 * keep a preview selection. Callers must only run this after settings and
 * role resolution succeeded — never on a partial or errored state.
 */
export const resolveStoredLanguage = (
  stored: StoredLanguage,
  available: boolean,
): { language: "en" | "ar"; downgrade: boolean } => ({
  language: stored === "ar" && available ? "ar" : "en",
  downgrade: stored === "ar" && !available,
});
